<template>
  <div v-if="user" class="min-h-screen bg-gray-100">
    <SidebarMenu
      :user="user"
      :isMenuOpen="isMenuOpen"
      @toggle-menu="toggleMenu"
      @close-menu="closeMenu"
      @logout="logout"
    />

    <div class="md:ml-64">
      <main class="p-4">
        <div class="p-4">
          <h1 class="text-xl font-bold">Normatividad</h1>
          <p class="text-gray-600 mb-4">Matrícula: {{ studentId }}</p>

          <div v-if="loading" class="flex justify-center my-8">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>

          <div v-else-if="error" class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {{ error }}
          </div>

          <div v-else-if="documents && documents.length > 0" class="mb-6">

            <h2 class="text-lg font-semibold mb-2">Documentos de Normatividad:</h2>
            
            <!-- Vista móvil: tarjetas individuales -->
            <div class="grid grid-cols-1 gap-4 sm:hidden">
              <div 
                v-for="(item, index) in documents" 
                :key="index"
                class="bg-white shadow overflow-hidden rounded-md p-4 border-l-4 border-blue-500"
              >
                <div class="grid grid-cols-1 gap-y-3">
                  <div>
                    <div class="text-sm font-medium text-gray-500 mb-1">Documento:</div>
                    <div class="text-sm font-medium text-gray-900">{{ item.document }}</div>
                  </div>

                  <div class="flex flex-col space-y-2 pt-2">
                    <button 
                      @click="viewDocument(item)"
                      :disabled="loadingDocument"
                      class="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                      </svg>
                      Vista previa
                    </button>
                    
                    <button 
                      @click="openInNewTab(item)"
                      :disabled="loadingDocument"
                      class="w-full inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                      </svg>
                      Abrir en nueva pestaña
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <!-- Vista escritorio: tabla tradicional -->
            <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg hidden sm:block">
              <table class="min-w-full divide-y divide-gray-300">
                <thead class="bg-gray-50">
                  <tr>
                    <th scope="col" class="px-6 py-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Documento</th>
                    <th scope="col" class="px-3 py-3 text-center text-xs font-medium uppercase tracking-wide text-gray-500">Acciones</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 bg-white text-sm font-medium text-gray-900">
                  <tr 
                    v-for="(item, index) in documents" 
                    :key="index"
                    class="hover:bg-gray-50"
                  >
                    <td class="px-6 py-4 text-sm text-gray-900">
                      {{ item.document }}
                    </td>
                    <td class="whitespace-nowrap px-3 py-4 text-center">
                      <div class="flex justify-center space-x-2">
                        <div class="tooltip-wrapper">
                          <button 
                            @click="viewDocument(item)"
                            :disabled="loadingDocument"
                            class="inline-flex items-center justify-center p-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400"
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                            </svg>
                          </button>
                          <div class="tooltip tooltip-details">
                            <span class="tooltip-text">Vista previa</span>
                            <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                              <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                            </svg>
                          </div>
                        </div>
                        
                        <div class="tooltip-wrapper">
                          <button 
                            @click="openInNewTab(item)"
                            :disabled="loadingDocument"
                            class="inline-flex items-center justify-center p-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100"
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                            </svg>
                          </button>
                          <div class="tooltip tooltip-details">
                            <span class="tooltip-text">Abrir en nueva pestaña</span>
                            <svg class="tooltip-arrow" width="16" height="10" viewBox="0 0 16 10">
                              <path d="M8 0L0 10H16L8 0Z" fill="currentColor" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          
          <div v-else class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            No hay documentos de normatividad disponibles para mostrar.
          </div>

          <!-- Mensajes de estado -->
          <div v-if="successMessage" class="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            {{ successMessage }}
          </div>
        </div>
      </main>
    </div>

    <!-- Modal de Vista Previa -->
    <NormatividadModal
      :isVisible="showModal"
      :selectedDocument="selectedDocument"
      @close="closeModal"
    />
  </div>
</template>

<script>
import axios from 'axios';
import SidebarMenu from '@/components/SidebarMenu.vue';
import NormatividadModal from '@/components/NormatividadModal.vue';
import { getLegalFiles } from "@/services/api";

export default {
  components: {
    SidebarMenu,
    NormatividadModal
  },
  data() {
    return {
      user: null,
      isMenuOpen: false,
      documents: null,
      loading: true,
      error: null,
      studentId: null,
      loadingDocument: false,
      successMessage: '',
      showModal: false,
      selectedDocument: null
    };
  },
  async created() {
    await this.getUserFromBackend();
  },
  methods: {
    async getUserFromBackend() {
      try {
        const response = await axios.get('/me');
        this.user = response.data.user;
        
        // Extraer el ID de estudiante
        if (this.user.email && this.user.email.match(/^a(\d+)@/)) {
          this.studentId = this.user.email.match(/^a(\d+)@/)[1];

          // Cargar datos con el ID extraído
          await this.loadData();
        } else {
          this.error = "No se pudo extraer el ID de estudiante del email.";
          this.loading = false;
        }
      } catch (error) {
        console.error("Error obteniendo usuario", error);
        this.$router.push('/login');
      }
    },

    async loadData() {
      try {
        const response = await getLegalFiles({ idstudent: this.studentId });
        this.documents = response.data;
        this.loading = false;
      } catch (error) {
        console.error("Error al obtener los documentos de normatividad:", error);
        this.error = "Error al cargar los documentos de normatividad. Por favor, intente nuevamente.";
        this.loading = false;
      }
    },

    viewDocument(document) {
      this.selectedDocument = document;
      this.showModal = true;
    },

    downloadDocument(document) {
      this.loadingDocument = true;
      this.successMessage = '';
      
      try {
        // Crear un enlace temporal para descargar
        const link = document.createElement('a');
        link.href = document.medialink;
        link.download = document.document;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Añadir al DOM temporalmente
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        this.successMessage = `Descarga de "${document.document}" iniciada.`;
        setTimeout(() => {
          this.successMessage = '';
        }, 3000);
      } catch (error) {
        console.error('Error iniciando descarga:', error);
        this.error = 'Error al iniciar la descarga.';
      } finally {
        this.loadingDocument = false;
      }
    },

    openInNewTab(document) {
      if (document.medialink) {
        window.open(`https://docs.google.com/gview?url=${encodeURIComponent(document.medialink)}&embedded=true`, '_blank', 'noopener,noreferrer');
      }
    },

    closeModal() {
      this.showModal = false;
      this.selectedDocument = null;
    },

    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },

    closeMenu() {
      this.isMenuOpen = false;
    },

    async logout() {
      try {
        // Obtener nuevo token CSRF
        await axios.get('/sanctum/csrf-cookie');

        // Esperar un momento para asegurar que la cookie se haya establecido
        await new Promise(resolve => setTimeout(resolve, 100));

        // Realizar la petición de logout
        const response = await axios.post('/logout', {}, {
          withCredentials: true,
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });
        console.log(response);
        // Limpiar cookies locales
        document.cookie.split(";").forEach(function(c) { 
          document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        // Redirigir al login
        this.$router.push('/login');
      } catch (error) {
        console.error('Error al cerrar sesión:', error.response?.data || error);
      }
    }
  }
};
</script>